import { useState } from "react";
import { trpc } from "@/hooks/trpc";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  FileSignature,
  Loader2,
  AlertTriangle,
  Check,
  CheckCircle2,
  Download,
  QrCode,
  Replace,
  X,
  XCircle,
} from "lucide-react";
import QRCode from "react-qr-code";
import { toast } from "sonner";
import { format } from "date-fns";
import { IntakeQuestionsConfig } from "./intake-questions-config";
import { NumericInput } from "@/components/ui/numeric-input";
import { useAutosave } from "@/hooks/use-autosave";

export function SensoryConfigSection() {
  const { data: config, isLoading } =
    trpc.food.sensory.config.get.useQuery();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-0.5">
        <h2 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Sensory Evaluation
        </h2>
        <p className="text-xs text-muted-foreground">
          Configure settings for sensory analysis sessions.
        </p>
      </div>

      <IntakeQuestionsConfig />

      <ExposureLimitConfig limit={config?.exposure_limit_g ?? null} />

      <ConsentDocumentConfig
        activeTemplate={config?.consentTemplate ?? null}
      />
    </div>
  );
}

// ─── Exposure limit ────────────────────────────────────────────────────────────

/** The approved Farmless intake per taster per tasting, which tasting portions are checked against. */
function ExposureLimitConfig({ limit }: { limit: string | null }) {
  const { save } = useAutosave({
    mutation: trpc.food.sensory.config.setExposureLimit,
    query: trpc.food.sensory.config.get,
    queryInput: undefined,
    updateCache: (input, old) => (old ? { ...old, exposure_limit_g: input.exposure_limit_g } : old),
    onError: (err) => toast.error(err.message),
  });

  return (
    <div className="max-w-3xl space-y-3">
      <div className="space-y-0.5">
        <p className="text-sm font-medium">Exposure limit</p>
        <p className="text-xs text-muted-foreground">
          Grams of Farmless ingredient one taster may have in one tasting. A tasting counts as one exposure. The editor
          warns when the portions add up to more. One limit for every Farmless variant until Clear and Classic are
          recorded on batches.
        </p>
      </div>
      <div className="flex items-center gap-2 text-sm">
        <NumericInput
          precise
          min={0}
          value={limit}
          onValueChange={(v) => save({ exposure_limit_g: v })}
          placeholder="Not set"
          postfix="g"
          className="h-8 w-28"
        />
        <span className="text-xs text-muted-foreground">per taster, per tasting</span>
      </div>
    </div>
  );
}

// ─── Consent Document Config ───────────────────────────────────────────────────

function ConsentDocumentConfig({
  activeTemplate,
}: {
  activeTemplate: {
    id: number;
    name: string;
    share_code: string;
    created_at: Date | string | null;
  } | null;
}) {
  const utils = trpc.useUtils();
  const [showSetup, setShowSetup] = useState(false);
  const [showReplace, setShowReplace] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const [showDeactivate, setShowDeactivate] = useState(false);

  const deactivateMutation =
    trpc.food.sensory.config.removeConsentTemplate.useMutation({
      onSuccess: () => {
        utils.food.sensory.config.get.invalidate();
        setShowDeactivate(false);
        toast.success("Consent document deactivated.");
      },
      onError: (err) => toast.error(err.message),
    });

  if (!activeTemplate) {
    return (
      <div className="max-w-2xl space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium">Consent Document</p>
        </div>
        <div className="border rounded-lg p-6 text-center space-y-3">
          <FileSignature className="h-7 w-7 mx-auto text-muted-foreground/40" />
          <div className="space-y-1">
            <p className="text-sm font-medium">
              No consent document configured
            </p>
            <p className="text-xs text-muted-foreground">
              Analysts will not be required to sign a consent form before
              participating in sensory evaluations.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowSetup(true)}
          >
            Set Up Consent Document
          </Button>
        </div>

        <SelectTemplateDialog
          open={showSetup}
          onOpenChange={setShowSetup}
          title="Set Up Consent Document"
          description="Select a document template that analysts must sign before participating in sensory evaluations."
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="max-w-2xl">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium">Consent Document</p>
        </div>

        <div className="border rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded bg-green-100 flex items-center justify-center">
                <Check className="h-4 w-4 text-green-700" />
              </div>
              <div>
                <p className="text-sm font-medium">{activeTemplate.name}</p>
                <p className="text-xs text-muted-foreground">
                  Active since{" "}
                  {activeTemplate.created_at
                    ? format(new Date(activeTemplate.created_at), "dd MMM yyyy")
                    : "—"}
                </p>
              </div>
            </div>
            <Badge
              variant="outline"
              className="text-xs text-green-700 border-green-300 bg-green-50"
            >
              Active
            </Badge>
          </div>

          <div className="flex items-center gap-2 pt-1">
            <Button
              variant="outline"
              size="sm"
              className="text-xs"
              onClick={() => setShowQr(true)}
            >
              <QrCode className="h-3.5 w-3.5 mr-1" />
              QR Code
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="text-xs"
              onClick={() => setShowReplace(true)}
            >
              <Replace className="h-3.5 w-3.5 mr-1" />
              Replace
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-xs text-muted-foreground"
              onClick={() => setShowDeactivate(true)}
            >
              <X className="h-3.5 w-3.5 mr-1" />
              Deactivate
            </Button>
          </div>
        </div>

        {/* Replace Dialog */}
        <SelectTemplateDialog
          open={showReplace}
          onOpenChange={setShowReplace}
          title="Replace Consent Document"
          description="Select a different document template. Analysts who have already signed the current document will not need to re-sign unless required by the new template's version settings."
          excludeId={activeTemplate.id}
        />

        {/* Deactivate Dialog */}
        <Dialog open={showDeactivate} onOpenChange={setShowDeactivate}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Deactivate Consent Document</DialogTitle>
            </DialogHeader>
            <div className="flex items-start gap-3 py-2">
              <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
              <p className="text-sm text-muted-foreground">
                Analysts will no longer be required to sign a consent form
                before participating in sensory evaluations. Existing signed
                records will be preserved.
              </p>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowDeactivate(false)}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                size="sm"
                disabled={deactivateMutation.isPending}
                onClick={() => deactivateMutation.mutate()}
              >
                {deactivateMutation.isPending && (
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                )}
                Deactivate
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* QR Code Dialog */}
        <Dialog open={showQr} onOpenChange={setShowQr}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Consent QR Code</DialogTitle>
              <DialogDescription>
                Scan this QR code to sign the consent document without joining a
                sensory session.
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col items-center gap-4 py-4">
              <div className="bg-white p-5 rounded-lg border">
                <QRCode
                  value={`${window.location.origin}/consent/${activeTemplate.share_code}`}
                  size={240}
                />
              </div>
              <p className="text-xs text-muted-foreground text-center">
                {activeTemplate.name}
              </p>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Signed Records */}
      <div>
        <SignedRecordsSection templateId={activeTemplate.id} />
      </div>
    </div>
  );
}

// ─── Signed Records ────────────────────────────────────────────────────────────

function SignedRecordsSection({ templateId }: { templateId: number }) {
  const { data: records = [], isLoading } =
    trpc.documents.signedRecords.list.useQuery(
      { template_id: templateId as any },
    );

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium">Signed Records</p>
        <Badge variant="secondary" className="text-xs">
          {records.length} record{records.length !== 1 ? "s" : ""}
        </Badge>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        </div>
      ) : records.length === 0 ? (
        <div className="border rounded-lg p-6 text-center">
          <p className="text-xs text-muted-foreground">
            No signed records yet.
          </p>
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/30">
                <th className="text-left font-medium px-3 py-2 text-xs">Name</th>
                <th className="text-left font-medium px-3 py-2 text-xs">Email</th>
                <th className="text-left font-medium px-3 py-2 text-xs">Signed</th>
                <th className="text-left font-medium px-3 py-2 text-xs">Version</th>
                <th className="text-left font-medium px-3 py-2 text-xs">Status</th>
                <th className="text-right font-medium px-3 py-2 text-xs" />
              </tr>
            </thead>
            <tbody>
              {records.map((r) => (
                <SignedRecordRow key={r.id} record={r} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SignedRecordRow({
  record,
}: {
  record: {
    id: number;
    email: string;
    full_name: string;
    signed_at: Date | string;
    invalidated_at: Date | string | null;
    document_path: string | null;
    version_number: number;
    operator_name: string | null;
  };
}) {
  const downloadQuery = trpc.documents.signedRecords.downloadUrl.useQuery(
    { document_path: record.document_path! },
    { enabled: false },
  );

  const isInvalidated = !!record.invalidated_at;
  const displayName = record.full_name || record.operator_name || "Unknown";

  const handleDownload = async () => {
    if (!record.document_path) return;
    const result = await downloadQuery.refetch();
    if (result.data) {
      const link = document.createElement("a");
      link.href = result.data;
      link.download = `consent-${record.full_name.replace(/\s+/g, "-").toLowerCase()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <tr className="border-b last:border-0 hover:bg-muted/20">
      <td className="px-3 py-2">{displayName}</td>
      <td className="px-3 py-2 text-muted-foreground text-xs">{record.email}</td>
      <td className="px-3 py-2 text-muted-foreground text-xs">
        {format(new Date(record.signed_at), "dd MMM yyyy")}
      </td>
      <td className="px-3 py-2">
        <Badge variant="secondary" className="text-xs">
          v{record.version_number}
        </Badge>
      </td>
      <td className="px-3 py-2">
        {isInvalidated ? (
          <div className="flex items-center gap-1 text-xs text-amber-700">
            <XCircle className="h-3 w-3" />
            Invalidated
          </div>
        ) : (
          <div className="flex items-center gap-1 text-xs text-green-700">
            <CheckCircle2 className="h-3 w-3" />
            Valid
          </div>
        )}
      </td>
      <td className="px-3 py-2 text-right">
        {record.document_path && (
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
            onClick={handleDownload}
            disabled={downloadQuery.isFetching}
          >
            {downloadQuery.isFetching ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Download className="h-3 w-3" />
            )}
          </Button>
        )}
      </td>
    </tr>
  );
}

// ─── Select Template Dialog ────────────────────────────────────────────────────

function SelectTemplateDialog({
  open,
  onOpenChange,
  title,
  description,
  excludeId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  excludeId?: number;
}) {
  const utils = trpc.useUtils();
  const { data: templates = [], isLoading } =
    trpc.documents.templates.list.useQuery(undefined, { enabled: open });

  const setMutation =
    trpc.food.sensory.config.setConsentTemplate.useMutation({
      onSuccess: () => {
        utils.food.sensory.config.get.invalidate();
        onOpenChange(false);
        toast.success("Consent document activated.");
      },
      onError: (err) => toast.error(err.message),
    });

  const availableTemplates = excludeId
    ? templates.filter((t) => t.id !== excludeId)
    : templates;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        ) : availableTemplates.length === 0 ? (
          <div className="py-6 text-center">
            <p className="text-sm text-muted-foreground">
              No document templates available. Create one in Settings &rarr;
              Documents first.
            </p>
          </div>
        ) : (
          <div className="max-h-[300px] overflow-y-auto space-y-1">
            {availableTemplates.map((t) => (
              <button
                key={t.id}
                type="button"
                className="w-full flex items-center justify-between px-3 py-2.5 rounded-md hover:bg-muted/50 transition-colors text-left cursor-pointer"
                disabled={setMutation.isPending}
                onClick={() =>
                  setMutation.mutate({ template_id: t.id as any })
                }
              >
                <div>
                  <p className="text-sm font-medium">{t.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {t.created_at
                      ? format(new Date(t.created_at), "dd MMM yyyy")
                      : "—"}
                  </p>
                </div>
                {setMutation.isPending &&
                  setMutation.variables?.template_id === t.id && (
                    <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                  )}
              </button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
